import {inject, Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {ConfirmationService} from 'primeng/api';
import {translate} from '@jsverse/transloco';
import {
  ProviderScoresResponse,
  ProviderTenderControllerService,
  SubmitSubcriteriaAttributeScoresRequestParams,
  TemplateScoreControllerService,
  TemplateScoreInput,
  TemplateScoreView,
  TenderAttributeScoreInput,
  TenderPhaseType,
  TenderSubcriteriaAttributesResponse,
} from '@frontend/shared-api';
import {EvaluationCriteria} from '@frontend/directus';
import {AttributeTransformationService} from './attribute-transformation.service';
import {SubcriteriaDataService} from './subcriteria-data.service';
import {
  ECTab,
  ScoreMapEntry,
  SubcriteriaAttribute,
  SubcriteriaRenderer,
  SubcriteriaTab,
  TemplateData,
} from '../components/provider-questionnaire/provider-questionnaire.types';
import {TabStatusEnum} from '../tabs/tab-status.model';
import {EVALUATION_CRITERIA, isEC1} from '../constants/evaluation-criteria.constants';

@Injectable({
  providedIn: 'root',
})
export class QuestionnaireService {
  private readonly providerTenderController = inject(ProviderTenderControllerService);
  private readonly templateScoreController = inject(TemplateScoreControllerService);
  private readonly subcriteriaDataService = inject(SubcriteriaDataService);
  private readonly attributeTransformationService = inject(AttributeTransformationService);

  async loadQuestionnaireData(tenderId: number, phase: TenderPhaseType): Promise<ECTab[]> {
    const tenderData = await this.fetchTenderSubcriteriaAttributes(tenderId, phase);
    return await this.attributeTransformationService.transformTenderDataToECTabs(tenderData);
  }

  async transformProviderScoresToECTabs(providerScores: ProviderScoresResponse): Promise<ECTab[]> {
    return await this.attributeTransformationService.transformProviderScoresToECTabs(
      providerScores,
    );
  }

  calculateTabCompletionStatus(
    tabs: SubcriteriaTab[],
    savedProjectReferenceCount: number,
  ): TabStatusEnum[] {
    return tabs.map((tab) => {
      if (SubcriteriaRenderer.PROJECT_REFERENCES === tab.renderer) {
        return savedProjectReferenceCount > 0 ? TabStatusEnum.COMPLETED : TabStatusEnum.NOT_STARTED;
      }

      const allCompleted = tab.attributes.every((attr) => attr.score != null && !attr.hasChanges);
      const hasProgress = tab.attributes.some((attr) => attr.score != null);

      if (allCompleted) return TabStatusEnum.COMPLETED;
      if (hasProgress) return TabStatusEnum.IN_PROGRESS;
      return TabStatusEnum.NOT_STARTED;
    });
  }

  calculateOverallCompletionStatus(tabStatuses: TabStatusEnum[]): TabStatusEnum {
    if (tabStatuses.length === 0) return TabStatusEnum.NOT_STARTED;

    if (tabStatuses.every((status) => status === TabStatusEnum.COMPLETED)) {
      return TabStatusEnum.COMPLETED;
    }

    if (
      tabStatuses.some(
        (status) => status === TabStatusEnum.IN_PROGRESS || status === TabStatusEnum.COMPLETED,
      )
    ) {
      return TabStatusEnum.IN_PROGRESS;
    }

    return TabStatusEnum.NOT_STARTED;
  }

  async saveTabAttributes(
    tenderId: number,
    phase: TenderPhaseType,
    attributes: SubcriteriaAttribute[],
  ): Promise<void> {
    await this.submitAttributeScores(
      tenderId,
      phase,
      attributes.filter((attr) => attr.hasChanges),
    );
  }

  async submitAttributeScores(
    tenderId: number,
    phase: TenderPhaseType,
    attributes: SubcriteriaAttribute[],
  ): Promise<void> {
    const attributeScores: TenderAttributeScoreInput[] = attributes
      .filter((attr) => attr.scoreAbsent || attr.score != null)
      .map((attr) => this.toTenderAttributeScoreInput(attr));
    if (attributeScores.length === 0) return;

    const request: SubmitSubcriteriaAttributeScoresRequestParams = {
      tenderId,
      tenderPhase: phase,
      tenderAttributeScoresRequest: {attributeScores},
    };
    await firstValueFrom(this.providerTenderController.submitSubcriteriaAttributeScores(request));
  }

  async finishQuestionnaireForPhase(tenderId: number, phase: TenderPhaseType): Promise<void> {
    await firstValueFrom(
      this.providerTenderController.finishTenderPhase({
        tenderId,
        tenderPhase: phase,
      }),
    );
  }

  showFinishConfirmation(
    phaseType: TenderPhaseType,
    confirmationService: ConfirmationService,
    onAccept: () => void,
  ): void {
    const phase = phaseType.toLowerCase();
    confirmationService.confirm({
      header: translate(`provider.${phase}.questionnaire.confirmDialog.title`),
      message: translate(`provider.${phase}.questionnaire.confirmDialog.message`),
      icon: 'pi pi-exclamation-triangle',
      accept: onAccept,
    });
  }

  async loadTemplateScoreMap(
    toolId: number,
    phase: TenderPhaseType,
  ): Promise<Map<string, ScoreMapEntry>> {
    const templateScores = await firstValueFrom(
      this.templateScoreController.getTemplateScores({toolId, phase}),
    );
    return this.buildScoreMap(templateScores.templateScores ?? []);
  }

  private toTenderAttributeScoreInput(attr: SubcriteriaAttribute): TenderAttributeScoreInput {
    return {
      attributeId: attr.attributeId,
      score: attr.scoreAbsent ? 0 : attr.score,
      scoreAbsent: attr.scoreAbsent,
      comment: attr.comment,
      capabilityLevel: attr.scoreAbsent ? undefined : attr.capabilityLevel,
      provisioningType: attr.scoreAbsent ? undefined : attr.provisioningType,
    };
  }

  private async fetchTenderSubcriteriaAttributes(
    tenderId: number,
    phase: TenderPhaseType,
  ): Promise<TenderSubcriteriaAttributesResponse> {
    return firstValueFrom(
      this.providerTenderController.getTenderSubcriteriaAttributes({
        tenderId,
        tenderPhase: phase,
      }),
    );
  }

  async loadTemplateData(
    toolId: number,
    phase: TenderPhaseType,
    supportedModuleFunctionIds?: number[],
  ): Promise<TemplateData> {
    const moduleFunctionIds =
      phase === TenderPhaseType.RFP ? supportedModuleFunctionIds : undefined;
    const [directusData, templateScores] = await Promise.all([
      this.subcriteriaDataService.loadAllDirectusData(),
      firstValueFrom(this.templateScoreController.getTemplateScores({toolId, phase})),
    ]);

    const scoresMap = this.buildScoreMap(templateScores.templateScores || []);
    const relevantECs = this.filterECsByPhase(directusData.directusEvaluationCriteria, phase);

    const syntheticTenderData = this.attributeTransformationService.createTemplateTenderData(
      directusData.directusAttributes,
      directusData.directusSubcriteria,
      relevantECs,
      scoresMap,
    );

    const ecTabs: ECTab[] = await this.attributeTransformationService.transformTenderDataToECTabs(
      syntheticTenderData,
      {
        supportedModuleFunctionIds: moduleFunctionIds,
      },
    );
    const templateData: TemplateData = {
      ecTabs: ecTabs,
      projectReferenceCount: templateScores.projectReferenceCount,
    };

    return templateData;
  }

  async saveTemplateScores(
    toolId: number,
    phase: TenderPhaseType,
    attributes: SubcriteriaAttribute[],
  ): Promise<void> {
    const templateScores: TemplateScoreInput[] = attributes
      .filter((attr) => attr.hasChanges && attr.score !== undefined && attr.score !== null)
      .map((attr) => ({
        toolId,
        attributeDirectusId: attr.directusId,
        score: attr.score,
        scoreAbsent: attr.scoreAbsent,
        comment: attr.comment,
        capabilityLevel: attr.scoreAbsent ? undefined : attr.capabilityLevel,
        provisioningType: attr.scoreAbsent ? undefined : attr.provisioningType,
      }));

    if (templateScores.length === 0) return;

    await firstValueFrom(
      this.templateScoreController.saveTemplateScores({
        templateScoresRequest: {templateScores},
        phase,
      }),
    );
  }

  private filterECsByPhase(
    criteria: EvaluationCriteria[],
    phase: TenderPhaseType,
  ): EvaluationCriteria[] {
    switch (phase) {
      case TenderPhaseType.RFI:
        return criteria.filter((ec) => isEC1(ec.ecNumber));

      case TenderPhaseType.RFP:
        return criteria.filter(
          (ec) => ec.ecNumber >= EVALUATION_CRITERIA.EC2 && ec.ecNumber <= EVALUATION_CRITERIA.EC7,
        );

      default:
        return [];
    }
  }

  buildScoreMap(templateScores: TemplateScoreView[]): Map<string, ScoreMapEntry> {
    return new Map(
      templateScores.map((s) => [
        s.attributeDirectusId,
        {
          score: s.score,
          scoreAbsent: s.scoreAbsent,
          comment: s.comment,
          capabilityLevel: s.capabilityLevel,
          provisioningType: s.provisioningType,
          singleStage: s.singleStage,
        },
      ]),
    );
  }
}
